import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Volume2, VolumeX, Play, Pause,
  Waves, UtensilsCrossed, BedDouble, Leaf,
  Thermometer, Sun, Home as HomeIcon, CalendarCheck, Compass, MessageCircle, Sparkles,
} from "lucide-react";
import InternalNav from "@/components/InternalNav";
import InlineMediaButton from "@/components/InlineMediaButton";
import { resolveSrc } from "@/components/InlineGalleryEditor";
import {
  RemoveEncartBtn, AddEncartBar, RestoreHiddenBar, AddSpaceModal, AddSimpleModal,
  ICON_MAP, resolveMedia,
} from "@/components/RefugeEncartEditors";
import { MEDIA } from "@/content/media";
import { useContent } from "@/lib/ContentProvider";

const DEFAULT_SPACE_LABELS = {
  bassin: "Le bassin minéral",
  cuisine: "La cuisine d'été",
  chambre: "La chambre",
  jardin: "Le jardin tropical",
};

const STATIC_IMG = {
  bassin: MEDIA.refuge.bassin,
  bassinChambre: MEDIA.refuge.bassinVueChambre,
  bassinEnsemble: MEDIA.refuge.bassinVueEnsemble,
  cuisine: MEDIA.refuge.cuisine,
  chambre: MEDIA.refuge.chambre,
  chambre3voy: MEDIA.refuge.chambre3voyageurs,
  berceau: MEDIA.refuge.chambreBerceau,
  jardin: MEDIA.refuge.jardin,
  jardinEntree: MEDIA.refuge.jardinEntree,
};
const STATIC_VIDEO = {
  webm: MEDIA.videos.refugeHeroWebm,
  mp4: MEDIA.videos.refugeHeroMp4,
};

export default function Refuge() {
  const { mediaUrl, buildWhatsAppUrl, content, saveText } = useContent();
  const [spaceModal, setSpaceModal] = useState(false);
  const [simpleModal, setSimpleModal] = useState(null); // "essentiel" | "bonasavoir" | null
  const IMG = Object.fromEntries(Object.entries(STATIC_IMG).map(([k, v]) => [k, mediaUrl(v)]));
  const VIDEO = Object.fromEntries(Object.entries(STATIC_VIDEO).map(([k, v]) => [k, mediaUrl(v)]));
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  // Overrides CMS pour le hero de la page Refuge — édition inline admin.
  // Fallback silencieux vers les fichiers statiques WebM/MP4/poster si non défini.
  const cmsRefuge = content?.refuge || {};
  const heroVideoOverride = cmsRefuge.heroVideoFile
    ? resolveSrc(cmsRefuge.heroVideoFile)
    : null;
  const heroPosterOverride = cmsRefuge.heroPosterFile
    ? resolveSrc(cmsRefuge.heroPosterFile)
    : null;
  const heroNoVideo = !!cmsRefuge.heroNoVideo;
  const posterUrl = heroPosterOverride || IMG.bassin;

  useEffect(() => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.play().catch(() => {});
    else videoRef.current.pause();
  }, [playing]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  // ===== Encarts dynamiques (édition inline admin) =====
  const hiddenSpaces = cmsRefuge.hiddenSpaces || [];
  const customSpaces = cmsRefuge.customSpaces || [];
  const hiddenEssentiel = cmsRefuge.hiddenEssentiel || [];
  const customEssentiel = cmsRefuge.customEssentiel || [];
  const hiddenBonasavoir = cmsRefuge.hiddenBonasavoir || [];
  const customBonasavoir = cmsRefuge.customBonasavoir || [];

  const hideDefault = (key, id) => {
    const cur = cmsRefuge[key] || [];
    if (!cur.includes(id)) saveText(`refuge.${key}`, [...cur, id]);
  };
  const restoreDefault = (key, id) => {
    const cur = cmsRefuge[key] || [];
    saveText(`refuge.${key}`, cur.filter((x) => x !== id));
  };
  const addCustom = (key, item) => {
    const cur = cmsRefuge[key] || [];
    saveText(`refuge.${key}`, [...cur, item]);
  };
  const removeCustom = (key, id) => {
    const cur = cmsRefuge[key] || [];
    saveText(`refuge.${key}`, cur.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="refuge-page" className="bg-refuge-bg min-h-screen">
      <InternalNav />

      {/* ===== HERO VIDÉO ===== */}
      <section
        data-testid="hero-video"
        className="relative w-full aspect-[4/5] max-h-[78vh] md:aspect-auto md:h-[92vh] md:max-h-none md:min-h-[560px] overflow-hidden bg-refuge-text"
      >
        <video
          ref={videoRef}
          key={heroVideoOverride || "static"}
          playsInline
          autoPlay
          loop
          muted
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          poster={posterUrl}
          style={heroNoVideo ? { display: "none" } : {}}
        >
          {heroVideoOverride ? (
            <>
              <source src={heroVideoOverride} type="video/mp4" />
              <source src={heroVideoOverride} type="video/quicktime" />
            </>
          ) : (
            <>
              <source src={VIDEO.webm} type="video/webm" />
              <source src={VIDEO.mp4} type="video/mp4" />
            </>
          )}
        </video>
        {heroNoVideo && (
          <img
            src={posterUrl}
            alt="Le Curieux Refuge"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Boutons admin d'édition — vidéo, poster, toggle vidéo/photo fixe */}
        <InlineMediaButton
          label="Vidéo hero"
          position="tl"
          accept="video/*"
          currentValue={cmsRefuge.heroVideoFile || "refuge-hero.mp4 (statique)"}
          onPick={(filename) => saveText("refuge.heroVideoFile", filename)}
          allowClear
          onClear={() => saveText("refuge.heroNoVideo", !heroNoVideo)}
          dataTestid="refuge-hero-video-edit"
        />
        <InlineMediaButton
          label="Poster hero"
          position="tc"
          accept="image/*"
          currentValue={cmsRefuge.heroPosterFile || "bassin.jpg (statique)"}
          onPick={(filename) => saveText("refuge.heroPosterFile", filename)}
          dataTestid="refuge-hero-poster-edit"
        />
        {heroNoVideo && (
          <div className="absolute top-36 left-3 z-30 px-2 py-1 rounded-full bg-black/60 text-white text-[10px] font-mono backdrop-blur-sm">
            Vidéo hero désactivée · seule la photo est affichée
          </div>
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.20) 50%, rgba(0,0,0,0.45) 100%)" }}
        />
        <div className="absolute bottom-6 right-6 md:bottom-8 md:right-8 z-10 flex items-center gap-3">
          <button
            type="button"
            data-testid="hero-sound-btn"
            onClick={() => setMuted(!muted)}
            className="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center bg-white/10 backdrop-blur-md border border-white/25 hover:bg-white/20 transition-all"
            aria-label={muted ? "Activer le son" : "Couper le son"}
          >
            {muted ? <VolumeX className="w-4 h-4 md:w-5 md:h-5 text-white" strokeWidth={1.2} /> : <Volume2 className="w-4 h-4 md:w-5 md:h-5 text-white" strokeWidth={1.2} />}
          </button>
          <button
            type="button"
            data-testid="hero-play-btn"
            onClick={() => setPlaying(!playing)}
            className="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center bg-white/10 backdrop-blur-md border border-white/25 hover:bg-white/20 transition-all"
            aria-label={playing ? "Mettre en pause" : "Lancer la vidéo"}
          >
            {playing ? <Pause className="w-4 h-4 md:w-5 md:h-5 text-white fill-white" strokeWidth={1.2} /> : <Play className="w-4 h-4 md:w-5 md:h-5 text-white fill-white" strokeWidth={1.2} />}
          </button>
        </div>
      </section>

      {/* ===== INTRO ===== */}
      <section className="bg-refuge-bg py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="font-heading italic font-light text-refuge-text/90 text-lg md:text-2xl leading-relaxed mb-3">
            Un lieu atypique et légèrement enchanté
          </p>
          <p className="font-body text-base md:text-lg text-refuge-soft/90 leading-relaxed">
            Un refuge chaleureux, pensé pour ralentir — une expérience sensorielle douce et naturelle.
          </p>
        </div>
      </section>

      {/* ===== CHAQUE ESPACE ===== */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-5xl font-light text-refuge-text text-center mb-16 md:mb-20 leading-tight">
            Chaque espace, <span className="italic">une atmosphère</span>
          </h2>

          <div className="space-y-20 md:space-y-28">
            {/* BASSIN */}
            {!hiddenSpaces.includes("bassin") && (
            <div id="section-bassin" className="scroll-mt-24 relative">
              <RemoveEncartBtn onRemove={() => hideDefault("hiddenSpaces", "bassin")} className="top-0 right-0" dataTestid="remove-space-bassin" />
              <div data-testid="space-le-bassin-minéral" className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <div>
                  <div className="overflow-hidden rounded-2xl border border-refuge-border/60" style={{ boxShadow: "0 18px 40px -24px rgba(45,40,37,0.35)" }}>
                    <img alt="Bassin minéral extérieur sous parasol en chaume avec hamac et pouf en jute" loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={IMG.bassin} />
                  </div>
                  <div className="mt-4 grid gap-4 grid-cols-2">
                    <figure className="overflow-hidden rounded-xl border border-refuge-border/60">
                      <img alt="Vue sur la chambre" loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={IMG.bassinChambre} />
                      <figcaption className="font-body text-[10px] tracking-[0.2em] uppercase text-refuge-soft px-3 py-2 bg-white">Vue sur la chambre</figcaption>
                    </figure>
                    <figure className="overflow-hidden rounded-xl border border-refuge-border/60">
                      <img alt="Vue d'ensemble" loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={IMG.bassinEnsemble} />
                      <figcaption className="font-body text-[10px] tracking-[0.2em] uppercase text-refuge-soft px-3 py-2 bg-white">Vue d'ensemble</figcaption>
                    </figure>
                  </div>
                </div>
                <div className="md:pl-4">
                  <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4">Le bassin minéral</p>
                  <h3 className="font-heading text-3xl md:text-4xl font-light text-refuge-text mb-3 leading-tight">Le bassin minéral</h3>
                  <p className="font-heading italic font-light text-refuge-soft/80 text-base md:text-lg mb-5">Chauffé par le soleil — écologique & atypique</p>
                  <p className="font-body text-base text-refuge-soft leading-relaxed max-w-md mb-5">
                    Le bassin minéral du refuge vous invite en plein air, au cœur d'un petit jardin tropical. L'environnement naturel, le calme du jardin et le contact avec l'eau créent un moment propice à la détente, à la déconnexion et au ralentissement.
                  </p>
                  <p className="font-body text-xs tracking-wide text-refuge-soft/80 italic max-w-md leading-relaxed border-l-2 border-refuge-rose/50 pl-4">
                    À savoir : le bassin est chauffé par un chauffe-eau solaire — jusqu'à 400 L d'eau chaude produits chaque jour selon la météo. Une expérience naturelle et écologique.
                  </p>
                </div>
              </div>
            </div>
            )}

            {/* CUISINE */}
            {!hiddenSpaces.includes("cuisine") && (
            <div id="section-cuisine" className="scroll-mt-24 relative">
              <RemoveEncartBtn onRemove={() => hideDefault("hiddenSpaces", "cuisine")} className="top-0 right-0" dataTestid="remove-space-cuisine" />
              <div data-testid="space-la-cuisine-d'été" className="grid md:grid-cols-2 gap-10 md:gap-16 items-center md:[&>*:first-child]:order-2">
                <div>
                  <div className="overflow-hidden rounded-2xl border border-refuge-border/60" style={{ boxShadow: "0 18px 40px -24px rgba(45,40,37,0.35)" }}>
                    <img alt="Cuisine d'été atypique avec mur rose, table ronde et voilage" loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={IMG.cuisine} />
                  </div>
                </div>
                <div className="md:pr-4">
                  <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4">La cuisine d'été</p>
                  <h3 className="font-heading text-3xl md:text-4xl font-light text-refuge-text mb-3 leading-tight">La cuisine d'été</h3>
                  <p className="font-heading italic font-light text-refuge-soft/80 text-base md:text-lg mb-5">Atypique et rose</p>
                  <p className="font-body text-base text-refuge-soft leading-relaxed max-w-md mb-5">
                    Une cuisine d'été couverte et ouverte sur le jardin. Une terrasse, un coin de vie, une table à manger — l'endroit où l'on prend le temps, entre dedans et dehors.
                  </p>
                  <ul className="space-y-2.5 mb-5 max-w-md">
                    {["Terrasse couverte","Coin de vie lumineux","Table à manger ouverte sur le jardin"].map((t)=>(
                      <li key={t} className="flex items-start gap-3 font-body text-sm text-refuge-text">
                        <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-refuge-ruby shrink-0"/>
                        <span className="leading-relaxed">{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            )}

            {/* CHAMBRE */}
            {!hiddenSpaces.includes("chambre") && (
            <div id="section-chambre" className="scroll-mt-24 relative">
              <RemoveEncartBtn onRemove={() => hideDefault("hiddenSpaces", "chambre")} className="top-0 right-0" dataTestid="remove-space-chambre" />
              <div data-testid="space-la-chambre" className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <div>
                  <div className="overflow-hidden rounded-2xl border border-refuge-border/60" style={{ boxShadow: "0 18px 40px -24px rgba(45,40,37,0.35)" }}>
                    <img alt="Chambre cocon avec bougies, roses et lumière tamisée" loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={IMG.chambre} />
                  </div>
                  <div className="mt-4 grid gap-4 grid-cols-2">
                    <figure className="overflow-hidden rounded-xl border border-refuge-border/60">
                      <img alt="3 voyageurs" loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={IMG.chambre3voy} />
                      <figcaption className="font-body text-[10px] tracking-[0.2em] uppercase text-refuge-soft px-3 py-2 bg-white">3 voyageurs</figcaption>
                    </figure>
                    <figure className="overflow-hidden rounded-xl border border-refuge-border/60">
                      <img alt="Berceau possible" loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={IMG.berceau} />
                      <figcaption className="font-body text-[10px] tracking-[0.2em] uppercase text-refuge-soft px-3 py-2 bg-white">Berceau possible</figcaption>
                    </figure>
                  </div>
                </div>
                <div className="md:pl-4">
                  <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4">La chambre</p>
                  <h3 className="font-heading text-3xl md:text-4xl font-light text-refuge-text mb-3 leading-tight">La chambre</h3>
                  <p className="font-heading italic font-light text-refuge-soft/80 text-base md:text-lg mb-5">Cocon doux et lumineux</p>
                  <p className="font-body text-base text-refuge-soft leading-relaxed max-w-md mb-5">
                    Une chambre pensée pour le repos, où la lumière du matin se pose doucement sur les matières naturelles.
                  </p>
                  <ul className="space-y-2.5 mb-5 max-w-md">
                    {[
                      "2 voyageurs — lit double 140 × 190",
                      "Rétroprojecteur pour des soirées cinéma",
                      "3 voyageurs — lit d'appoint possible",
                      "Moment spécial — chambre sublimée par les décors du refuge",
                    ].map((t)=>(
                      <li key={t} className="flex items-start gap-3 font-body text-sm text-refuge-text">
                        <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-refuge-ruby shrink-0"/>
                        <span className="leading-relaxed">{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            )}

            {/* JARDIN */}
            {!hiddenSpaces.includes("jardin") && (
            <div id="section-jardin" className="scroll-mt-24 relative">
              <RemoveEncartBtn onRemove={() => hideDefault("hiddenSpaces", "jardin")} className="top-0 right-0" dataTestid="remove-space-jardin" />
              <div data-testid="space-le-jardin-tropical" className="grid md:grid-cols-2 gap-10 md:gap-16 items-center md:[&>*:first-child]:order-2">
                <div>
                  <div className="overflow-hidden rounded-2xl border border-refuge-border/60" style={{ boxShadow: "0 18px 40px -24px rgba(45,40,37,0.35)" }}>
                    <img alt="Vue plongeante du jardin tropical et de son sentier en pas japonais" loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={IMG.jardin} />
                  </div>
                  <div className="mt-4 grid gap-4 grid-cols-1">
                    <figure className="overflow-hidden rounded-xl border border-refuge-border/60">
                      <img alt="Entrée du jardin" loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={IMG.jardinEntree} />
                      <figcaption className="font-body text-[10px] tracking-[0.2em] uppercase text-refuge-soft px-3 py-2 bg-white">Entrée du jardin</figcaption>
                    </figure>
                  </div>
                </div>
                <div className="md:pr-4">
                  <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4">Le jardin</p>
                  <h3 className="font-heading text-3xl md:text-4xl font-light text-refuge-text mb-3 leading-tight">Le jardin tropical</h3>
                  <p className="font-heading italic font-light text-refuge-soft/80 text-base md:text-lg mb-5">Une respiration végétale</p>
                  <p className="font-body text-base text-refuge-soft leading-relaxed max-w-md mb-5">
                    Plantes tropicales, coins ombragés, chants d'oiseaux — le jardin prolonge chaque espace du refuge et invite à ralentir, simplement.
                  </p>
                </div>
              </div>
            </div>
            )}

            {/* ESPACES CUSTOM (ajoutés par l'admin) */}
            {customSpaces.map((s) => {
              const reversedCls = s.reversed ? "md:[&>*:first-child]:order-2" : "";
              return (
                <div key={s.id} className="scroll-mt-24 relative" data-testid={`space-custom-${s.id}`}>
                  <RemoveEncartBtn onRemove={() => removeCustom("customSpaces", s.id)} className="top-0 right-0" dataTestid={`remove-space-${s.id}`} />
                  <div className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${reversedCls}`}>
                    <div>
                      <div className="overflow-hidden rounded-2xl border border-refuge-border/60" style={{ boxShadow: "0 18px 40px -24px rgba(45,40,37,0.35)" }}>
                        <img alt={s.title} loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={resolveMedia(s.mainImage)} />
                      </div>
                      {s.layout === "gallery" && s.gallery?.length > 0 && (
                        <div className={`mt-4 grid gap-4 ${s.gallery.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                          {s.gallery.map((g, gi) => (
                            <figure key={gi} className="overflow-hidden rounded-xl border border-refuge-border/60">
                              <img alt={g.alt || ""} loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] hover:scale-[1.03]" src={resolveMedia(g.src)} />
                              {g.alt && <figcaption className="font-body text-[10px] tracking-[0.2em] uppercase text-refuge-soft px-3 py-2 bg-white">{g.alt}</figcaption>}
                            </figure>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={s.reversed ? "md:pr-4" : "md:pl-4"}>
                      {s.eyebrow && <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4">{s.eyebrow}</p>}
                      <h3 className="font-heading text-3xl md:text-4xl font-light text-refuge-text mb-3 leading-tight">{s.title}</h3>
                      {s.subtitle && <p className="font-heading italic font-light text-refuge-soft/80 text-base md:text-lg mb-5">{s.subtitle}</p>}
                      {s.text && <p className="font-body text-base text-refuge-soft leading-relaxed max-w-md mb-5">{s.text}</p>}
                      {s.bullets?.length > 0 && (
                        <ul className="space-y-2.5 mb-5 max-w-md">
                          {s.bullets.map((t) => (
                            <li key={t} className="flex items-start gap-3 font-body text-sm text-refuge-text">
                              <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-refuge-ruby shrink-0" />
                              <span className="leading-relaxed">{t}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <AddEncartBar label="Ajouter un espace" onClick={() => setSpaceModal(true)} dataTestid="add-space-btn" />
          <RestoreHiddenBar
            hidden={hiddenSpaces}
            labelFor={(id) => DEFAULT_SPACE_LABELS[id] || id}
            onRestore={(id) => restoreDefault("hiddenSpaces", id)}
            dataTestid="restore-spaces-bar"
          />
        </div>
      </section>

      {/* ===== L'ESSENTIEL — CE QUI VOUS ATTEND ===== */}
      <section className="bg-refuge-bg py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby text-center mb-4">L'essentiel</p>
          <h2 className="font-heading text-3xl md:text-5xl font-light text-refuge-text text-center mb-16 leading-tight">Ce qui vous attend</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
            {[
              { key: "bassin-minéral", label: "Bassin minéral", Icon: Waves },
              { key: "cuisine-d'été", label: "Cuisine d'été", Icon: UtensilsCrossed },
              { key: "chambre-cosy", label: "Chambre cosy", Icon: BedDouble },
              { key: "jardin-tropical", label: "Jardin tropical", Icon: Leaf },
            ].filter(({ key }) => !hiddenEssentiel.includes(key)).map(({ key, label, Icon }) => (
              <div key={key} data-testid={`essentiel-${key}`} className="group relative flex flex-col items-center text-center">
                <RemoveEncartBtn onRemove={() => hideDefault("hiddenEssentiel", key)} className="top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity" dataTestid={`remove-essentiel-${key}`} />
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-refuge-bg border border-refuge-border flex items-center justify-center mb-5" style={{ boxShadow: "0 4px 14px -8px rgba(45,40,37,0.15)" }}>
                  <Icon className="w-7 h-7 md:w-8 md:h-8 text-refuge-ruby" strokeWidth={1} />
                </div>
                <p className="font-body text-sm md:text-base text-refuge-text">{label}</p>
              </div>
            ))}
            {customEssentiel.map((c) => {
              const Icon = ICON_MAP[c.icon] || Sparkles;
              return (
                <div key={c.id} data-testid={`essentiel-custom-${c.id}`} className="group relative flex flex-col items-center text-center">
                  <RemoveEncartBtn onRemove={() => removeCustom("customEssentiel", c.id)} className="top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity" dataTestid={`remove-essentiel-${c.id}`} />
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-refuge-bg border border-refuge-border flex items-center justify-center mb-5" style={{ boxShadow: "0 4px 14px -8px rgba(45,40,37,0.15)" }}>
                    <Icon className="w-7 h-7 md:w-8 md:h-8 text-refuge-ruby" strokeWidth={1} />
                  </div>
                  <p className="font-body text-sm md:text-base text-refuge-text">{c.label}</p>
                </div>
              );
            })}
          </div>
          <AddEncartBar label="Ajouter une carte" onClick={() => setSimpleModal("essentiel")} dataTestid="add-essentiel-btn" />
          <RestoreHiddenBar
            hidden={hiddenEssentiel}
            onRestore={(id) => restoreDefault("hiddenEssentiel", id)}
            dataTestid="restore-essentiel-bar"
          />
        </div>
      </section>

      {/* ===== BON À SAVOIR ===== */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby text-center mb-3">Bon à savoir</p>
          <h2 className="font-heading text-2xl md:text-3xl font-light text-refuge-text text-center mb-10 leading-tight">Informations pratiques</h2>
          <ul data-testid="bonasavoir-list" className="divide-y divide-refuge-border/60 rounded-2xl border border-refuge-border/60 bg-white overflow-hidden">
            {[
              { Icon: Sun, text: "Le bassin est chauffé par un chauffe-eau solaire — jusqu'à 400 L / jour selon la météo." },
              { Icon: Volume2, text: "Un espace calme, pensé pour le repos. Merci de préserver cette atmosphère." },
              { Icon: HomeIcon, text: "L'intérieur et l'extérieur sont en continuité — le refuge se vit de part et d'autre." },
              { Icon: Leaf, text: "Le jardin est entretenu au fil des saisons, son aspect évolue au rythme de la nature." },
            ].map((row, i) => ({ ...row, id: String(i) })).filter(({ id }) => !hiddenBonasavoir.includes(id)).map(({ Icon, text, id }) => (
              <li key={id} data-testid={`bonasavoir-row-${id}`} className="group relative flex items-center gap-4 px-5 md:px-6 py-4">
                <div className="w-9 h-9 rounded-full bg-refuge-ruby/5 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-refuge-ruby" strokeWidth={1.2} />
                </div>
                <p className="font-body text-sm text-refuge-text/90 leading-snug pr-24">{text}</p>
                <RemoveEncartBtn onRemove={() => hideDefault("hiddenBonasavoir", id)} className="top-1/2 -translate-y-1/2 right-4 opacity-0 group-hover:opacity-100 transition-opacity" dataTestid={`remove-bonasavoir-${id}`} />
              </li>
            ))}
            {customBonasavoir.map((c) => {
              const Icon = ICON_MAP[c.icon] || Leaf;
              return (
                <li key={c.id} data-testid={`bonasavoir-custom-${c.id}`} className="group relative flex items-center gap-4 px-5 md:px-6 py-4">
                  <div className="w-9 h-9 rounded-full bg-refuge-ruby/5 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-refuge-ruby" strokeWidth={1.2} />
                  </div>
                  <p className="font-body text-sm text-refuge-text/90 leading-snug pr-24">{c.text}</p>
                  <RemoveEncartBtn onRemove={() => removeCustom("customBonasavoir", c.id)} className="top-1/2 -translate-y-1/2 right-4 opacity-0 group-hover:opacity-100 transition-opacity" dataTestid={`remove-bonasavoir-${c.id}`} />
                </li>
              );
            })}
          </ul>
          <AddEncartBar label="Ajouter une info pratique" onClick={() => setSimpleModal("bonasavoir")} dataTestid="add-bonasavoir-btn" />
          <RestoreHiddenBar
            hidden={hiddenBonasavoir}
            labelFor={(id) => `Info ${Number(id) + 1}`}
            onRestore={(id) => restoreDefault("hiddenBonasavoir", id)}
            dataTestid="restore-bonasavoir-bar"
          />
        </div>
      </section>

      {/* ===== PROLONGER LA VISITE ===== */}
      <section className="bg-refuge-cta py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6">
          <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby text-center mb-4">Prolonger la visite</p>
          <h2 className="font-heading text-3xl md:text-5xl font-light text-refuge-text text-center mb-14 leading-tight">Poursuivre la découverte</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7 mb-10">
            <Link to="/reserver" data-testid="orientation-dispo-cta" className="group block bg-white rounded-2xl border border-refuge-border px-7 py-8 h-full transition-all duration-500 hover:shadow-md hover:scale-[1.02] hover:border-refuge-rose/50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-refuge-bg border border-refuge-border flex items-center justify-center shrink-0">
                  <CalendarCheck className="w-5 h-5 text-refuge-ruby" strokeWidth={1.1} />
                </div>
                <div>
                  <p className="font-heading text-lg font-light text-refuge-text mb-1.5 leading-snug">Voir les disponibilités</p>
                  <p className="font-body text-sm text-refuge-soft leading-relaxed">Choisissez vos dates et préparez votre venue au refuge</p>
                </div>
              </div>
            </Link>
            <Link to="/experiences" data-testid="orientation-experiences-cta" className="group block bg-white rounded-2xl border border-refuge-border px-7 py-8 h-full transition-all duration-500 hover:shadow-md hover:scale-[1.02] hover:border-refuge-rose/50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-refuge-bg border border-refuge-border flex items-center justify-center shrink-0">
                  <Compass className="w-5 h-5 text-refuge-ruby" strokeWidth={1.1} />
                </div>
                <div>
                  <p className="font-heading text-lg font-light text-refuge-text mb-1.5 leading-snug">Explorer les expériences</p>
                  <p className="font-body text-sm text-refuge-soft leading-relaxed">Découvrez les décors et moments à ajouter à votre séjour</p>
                </div>
              </div>
            </Link>
            <a
              href={buildWhatsAppUrl("Bonjour, j'ai une question sur le Curieux Refuge.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="orientation-whatsapp-cta"
              className="group block bg-white rounded-2xl border border-refuge-border px-7 py-8 h-full transition-all duration-500 hover:shadow-md hover:scale-[1.02] hover:border-refuge-rose/50"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-refuge-bg border border-refuge-border flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5 text-refuge-ruby" strokeWidth={1.1} />
                </div>
                <div>
                  <p className="font-heading text-lg font-light text-refuge-text mb-1.5 leading-snug">Poser une question à l'hôte</p>
                  <p className="font-body text-sm text-refuge-soft leading-relaxed">Échangez en direct sur WhatsApp pour toute demande</p>
                </div>
              </div>
            </a>
          </div>

          <p className="font-body text-sm text-refuge-soft/90 max-w-lg mx-auto text-center leading-relaxed">
            Le refuge se visite au calme. Pour toute question, vous pouvez nous écrire avant votre séjour.
          </p>
        </div>
      </section>

      {/* Retour à l'accueil - lien discret bas de page */}
      <div className="bg-refuge-cta pb-10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Link
            to="/accueil"
            data-testid="back-to-hub"
            className="inline-flex items-center gap-2 font-body text-xs tracking-[0.18em] uppercase text-refuge-soft hover:text-refuge-ruby transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.3} />
            Retour à l'accueil
          </Link>
        </div>
      </div>

      {/* ===== MODALES D'AJOUT D'ENCART (admin) ===== */}
      {spaceModal && (
        <AddSpaceModal
          onClose={() => setSpaceModal(false)}
          onSave={(item) => { addCustom("customSpaces", item); setSpaceModal(false); }}
        />
      )}
      {simpleModal && (
        <AddSimpleModal
          type={simpleModal}
          onClose={() => setSimpleModal(null)}
          onSave={(item) => {
            addCustom(simpleModal === "essentiel" ? "customEssentiel" : "customBonasavoir", item);
            setSimpleModal(null);
          }}
        />
      )}
    </div>
  );
}
